import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../core/redux/api/reduxStandardApiAction';
import { apiGetGuestPaymentRegistrations } from '../../core/redux/api/endpoints';
import { IAppState } from '../../core/redux/IAppState';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../core/redux/api/circuitBreaker/module';
import { GuestPaymentRegistration } from './paymentRegistration';
import { GuestDetails } from '../guestDetails';
import { updateGuest } from '../module';
import * as R from 'ramda';


const KEY_ACTION = ACTION_TYPE.GET_GUEST_PAYMENT_REGISTRATION_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.GET_GUEST_PAYMENT_REGISTRATION_REQUEST,
  ACTION_TYPE.GET_GUEST_PAYMENT_REGISTRATION_RESPONSE,
  ACTION_TYPE.GET_GUEST_PAYMENT_REGISTRATION_ERROR,
];


/**
 * Constructs a Redux Action for getting the guest payment registrations for a specific guest id
 * @param guestId - The guest id for the guest whose payment registrations will be retrieved
 */
export const getGuestPaymentRegistrationsAction = (guest: GuestDetails) => makeApiAction(
  {
    method:   'GET',
    endpoint: apiGetGuestPaymentRegistrations(guest.masterGuestId),
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    types:    ACTIONS.map(action => ({ type: action, meta: { guest } })) as RsaaTypes,
    bailout:  state => !!getLoading(state, guest.masterGuestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<GuestPaymentRegistration[]> = (state, action, error, isLoading, payload) => {
  const metaGuest = action.meta.guest;
  const guestId   = metaGuest.masterGuestId;

  if (isLoading) {
    return addLoading(state, guestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, guestId, KEY_ACTION, error, { guest: metaGuest });
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  // If the guest has no payment registrations (204 - no content) - update the guest's payment registrations to an empty array
  if (R.isNil(payload)) {
    return updateGuest(noLoadingOrError, guestId, R.assocPath([ 'paymentRegistrations' ], []));
  }

  return updateGuest(noLoadingOrError, guestId, R.assocPath([ 'paymentRegistrations' ], payload));
};


export const getGuestPaymentRegistrationsReducer = makeApiReducer<GuestPaymentRegistration[]>(ACTIONS, KEY_ACTION, reducer);
